// Cache management utilities for shahsaa.com domain

export interface CacheClearOptions {
  clearLocalStorage?: boolean;
  clearSessionStorage?: boolean;
  clearCookies?: boolean;
  clearIndexedDB?: boolean;
  preserveAuthTokens?: boolean;
}

/**
 * Clears browser cache specifically for shahsaa.com domain
 */
export const clearShahsaaCache = async (options: CacheClearOptions = {}): Promise<{
  success: boolean;
  clearedItems: string[];
  errors: string[];
}> => {
  const {
    clearLocalStorage = true,
    clearSessionStorage = true,
    clearCookies = false,
    clearIndexedDB = false,
    preserveAuthTokens = true
  } = options;

  const clearedItems: string[] = [];
  const errors: string[] = [];

  try {
    // 1. Clear Cache API caches
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        const shahsaaCaches = cacheNames.filter(name => 
          name.includes('shahsaa') || 
          name.includes('vanaxi') ||
          name.includes('cache')
        );
        
        await Promise.all(
          shahsaaCaches.map(async (cacheName) => {
            await caches.delete(cacheName);
            clearedItems.push(`Cache: ${cacheName}`);
          })
        );
      } catch (error) {
        errors.push(`Cache API error: ${error}`);
      }
    }

    // 2. Clear localStorage (preserving auth tokens if requested)
    if (clearLocalStorage) {
      try {
        if (preserveAuthTokens) {
          const authToken = localStorage.getItem('auth_token');
          const refreshToken = localStorage.getItem('refresh_token');
          
          // Clear everything except auth tokens
          localStorage.clear();
          
          // Restore auth tokens
          if (authToken) localStorage.setItem('auth_token', authToken);
          if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
          
          clearedItems.push('LocalStorage (preserving auth tokens)');
        } else {
          localStorage.clear();
          clearedItems.push('LocalStorage (all data)');
        }
      } catch (error) {
        errors.push(`LocalStorage error: ${error}`);
      }
    }

    // 3. Clear sessionStorage
    if (clearSessionStorage) {
      try {
        sessionStorage.clear();
        clearedItems.push('SessionStorage');
      } catch (error) {
        errors.push(`SessionStorage error: ${error}`);
      }
    }

    // 4. Clear cookies (if supported and requested)
    if (clearCookies && 'cookieStore' in window) {
      try {
        const cookies = await (window as any).cookieStore.getAll();
        const shahsaaCookies = cookies.filter((cookie: any) => 
          cookie.name.includes('shahsaa') || 
          cookie.name.includes('vanaxi')
        );
        
        await Promise.all(
          shahsaaCookies.map((cookie: any) => 
            (window as any).cookieStore.delete(cookie.name)
          )
        );
        
        clearedItems.push(`Cookies: ${shahsaaCookies.length} items`);
      } catch (error) {
        errors.push(`Cookie clearing error: ${error}`);
      }
    }

    // 5. Clear IndexedDB (if requested)
    if (clearIndexedDB && 'indexedDB' in window) {
      try {
        const databases = await (window as any).indexedDB.databases();
        const shahsaaDatabases = databases.filter((db: any) => 
          db.name.includes('shahsaa') || 
          db.name.includes('vanaxi')
        );
        
        for (const db of shahsaaDatabases) {
          await (window as any).indexedDB.deleteDatabase(db.name);
          clearedItems.push(`IndexedDB: ${db.name}`);
        }
      } catch (error) {
        errors.push(`IndexedDB error: ${error}`);
      }
    }

    // 6. Clear axios default headers
    try {
      if (typeof window !== 'undefined' && (window as any).axios) {
        delete (window as any).axios.defaults.headers.common['Authorization'];
        clearedItems.push('Axios headers');
      }
    } catch (error) {
      errors.push(`Axios headers error: ${error}`);
    }

    return {
      success: true,
      clearedItems,
      errors
    };

  } catch (error) {
    errors.push(`General error: ${error}`);
    return {
      success: false,
      clearedItems,
      errors
    };
  }
};

/**
 * Gets cache information for debugging
 */
export const getCacheInfo = async (): Promise<{
  cacheNames: string[];
  localStorageKeys: string[];
  sessionStorageKeys: string[];
  cookieCount: number;
}> => {
  const info: {
    cacheNames: string[];
    localStorageKeys: string[];
    sessionStorageKeys: string[];
    cookieCount: number;
  } = {
    cacheNames: [],
    localStorageKeys: [],
    sessionStorageKeys: [],
    cookieCount: 0
  };

  try {
    // Get Cache API info
    if ('caches' in window) {
      info.cacheNames = await caches.keys();
    }

    // Get localStorage info
    if (typeof localStorage !== 'undefined') {
      info.localStorageKeys = Object.keys(localStorage);
    }

    // Get sessionStorage info
    if (typeof sessionStorage !== 'undefined') {
      info.sessionStorageKeys = Object.keys(sessionStorage);
    }

    // Get cookie count
    if (typeof document !== 'undefined') {
      info.cookieCount = document.cookie.split(';').length;
    }
  } catch (error) {
    console.error('Error getting cache info:', error);
  }

  return info;
};

/**
 * Checks if cache clearing is supported in the current browser
 */
export const isCacheClearingSupported = (): boolean => {
  return (
    'caches' in window ||
    typeof localStorage !== 'undefined' ||
    typeof sessionStorage !== 'undefined'
  );
};
